import { useState } from 'react'
import { Link } from 'react-router-dom'
import { ErrorBanner } from '@/components/AppLayout'
import { useAsync } from '@/hooks/useAsync'
import type { CanonicalEventRow } from '@/lib/eventbrite'
import { listEventbriteEvents } from '@/lib/eventbriteImport'
import { importEventbriteEvent } from '@/lib/queries'

interface Props {
  organizationId: string
  /** Eventbrite ids already linked to a local event. */
  linkedEventbriteIds: ReadonlySet<string>
  onImported: () => void
  onClose: () => void
}

function formatWhen(row: CanonicalEventRow): string {
  if (row.starts_at === null) return 'No date set'
  return new Date(row.starts_at).toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

const STATUS_BADGE: Record<CanonicalEventRow['status'], string> = {
  draft: 'badge',
  active: 'badge badge-accent',
  completed: 'badge badge-success',
  archived: 'badge badge-warning',
}

/**
 * Browse the Eventbrite account and create local events from it.
 *
 * Importing an event that is already linked is a no-op rather than an error —
 * the row just says "already imported" — so an organiser can re-open this list
 * without worrying about creating duplicates.
 */
export function EventbriteEventPicker({
  organizationId,
  linkedEventbriteIds,
  onImported,
  onClose,
}: Props) {
  const listing = useAsync(() => listEventbriteEvents(organizationId), [organizationId])
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [imported, setImported] = useState<Record<string, 'created' | 'existing'>>({})

  async function handleImport(row: CanonicalEventRow) {
    setBusyId(row.external_id)
    setError(null)
    try {
      const result = await importEventbriteEvent(organizationId, row)
      setImported((current) => ({
        ...current,
        [row.external_id]: result.created ? 'created' : 'existing',
      }))
      onImported()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not import that event')
    } finally {
      setBusyId(null)
    }
  }

  const rows = listing.data?.events ?? []

  return (
    <div className="card">
      <div className="card-header">
        <h2>Import from Eventbrite</h2>
        <button className="btn btn-sm btn-ghost" type="button" onClick={onClose}>
          Close
        </button>
      </div>

      {error !== null && <ErrorBanner message={error} />}
      {listing.error !== null && <ErrorBanner message={listing.error} />}

      {listing.data?.credential_source === 'stub' && (
        <p className="banner banner-warning">
          No Eventbrite account is connected, so this is sample data. Connect one in{' '}
          <Link to="/settings">Settings</Link> to see your real events.
        </p>
      )}

      {listing.data?.credential_source === 'deployment' && (
        <p className="banner banner-warning">
          These events come from a shared Eventbrite account configured for this deployment, not
          from this organization&rsquo;s own. Connect an account in{' '}
          <Link to="/settings">Settings</Link> to read your own instead.
        </p>
      )}

      {listing.loading ? (
        <p className="field-hint">Loading events from Eventbrite…</p>
      ) : rows.length === 0 ? (
        <p className="field-hint">
          No events came back from Eventbrite. Check that the connected account is the one running
          these events.
        </p>
      ) : (
        <div className="table-responsive">
          <div className="table-wrap">
            <table>
              <caption className="sr-only">Events available to import from Eventbrite</caption>
              <thead>
                <tr>
                  <th scope="col">Event</th>
                  <th scope="col">When</th>
                  <th scope="col">Status</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const outcome = imported[row.external_id]
                  const alreadyLinked = linkedEventbriteIds.has(row.external_id)

                  return (
                    <tr key={row.external_id}>
                      <td data-label="Event">{row.name}</td>
                      <td data-label="When" className="cell-muted">
                        {formatWhen(row)}
                      </td>
                      <td data-label="Status">
                        <span className={STATUS_BADGE[row.status]}>{row.status}</span>
                      </td>
                      <td data-label="">
                        {outcome === 'created' ? (
                          <span className="badge badge-success">Imported</span>
                        ) : outcome === 'existing' || alreadyLinked ? (
                          <span className="badge">Already imported</span>
                        ) : (
                          <button
                            className="btn btn-sm"
                            type="button"
                            disabled={busyId !== null}
                            onClick={() => void handleImport(row)}
                          >
                            {busyId === row.external_id ? 'Importing…' : 'Import'}
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="field-hint">
        Importing creates the event here with its name, dates and branding. Attendees are a separate
        step, on the event&rsquo;s Attendees tab. Re-importing an event never overwrites changes you
        have made locally.
      </p>
    </div>
  )
}
